import React, { useCallback, useMemo, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";

import { Chip, User } from "@nextui-org/react";
import { FaEye } from "react-icons/fa";
import { MdDelete, MdEdit } from "react-icons/md";

import {
    formatDateTime,
    secondsToTime,
    convertURLParams,
    parseSearchParams,
} from "@/utils/helpers";

import { useCourses } from "../hooks/useCourses";
import useDeleteCourse from "../hooks/useDeleteCourse";

import Modal from "@/components/Modal";
import ConfirmDelete from "@/components/ConfirmDelete";

import Table from "../../components/Table/Table";
import HeaderContent from "../../components/Table/components/HeaderContent";
import FooterContent from "../../components/Table/components/FooterContent";
import CreateForm from "./CreateForm";
import UpdateCourse from "./UpdateCourse";
import CourseBox from "./CourseView";
import { schoolLevelIds, STORAGE_LINK, translatedSchoolGrades, translatedSchoolLevels } from "../../../../utils/constants";
import usePermission from "../../../../hooks/usePermission";


function CoursesTable() {
    const { search } = useLocation();
    const [perPage, setPerPage] = useState(10);
    const [searchValue, setSearchValue] = useState("");
    const [searchParams] = useSearchParams();

    const { deleteCourse, isDeleting } = useDeleteCourse();

    const { canAll, can } = usePermission();
    //include

    const includeFields = canAll(["read:users", "read:course:lectures"])
        ? ["author", "lecturesCount"]
        : can("read:users")
            ? "author"
            : can("read:course:lectures")
                ? "lecturesCount"
                : null;

    const include = [
        { field: "VideosDuration", value: "true" },
        { field: "VideosCount", value: "true" },
        { field: "QuizzesCount", value: "true" },
        { field: "StudentsCount", value: "true" },
    ];

    const page = parseSearchParams(
        searchParams,
        "page",
        (value) => (parseInt(value, 10) < 1 ? 1 : parseInt(value, 10)),
        1
    );

    // filter , sort , page
    const filterAndSortAndPageQuery = convertURLParams(search);

    const { courses, isPending } = useCourses({
        searchValue,
        include,
        includeFields,
        perPage,
        page,
        filterAndSortAndPageQuery,
    });

    //filterOptions
    const filterOptions = [
        { name: "مرئي", field: "visibility", value: "yes" },
        { name: "مخفي", field: "visibility", value: "no" },
        { name: "المرحلة الإعدادية", field: "school_level", value: "2" },
        { name: "المرحلة الثانوية", field: "school_level", value: "3" },
    ];

    const filterButtons = [
        { field: "type", type: "online", value: "1", label: "اونلاين" },
        { field: "type", type: "offline", value: "2", label: "سنتر" },
    ];
    //headerRows
    const headers = canAll(["read:users", "read:course:lectures"])
        ? [
            { uid: "id", name: "#", sortable: true },
            { uid: "courseName", name: "اسم الكورس", sortable: false },
            { uid: "grade", name: "الصف", sortable: true },
            { uid: "visibility", name: "الحاله", sortable: true },
            { uid: "typeOfCourse", name: "نوع الكورس", sortable: false },
            { uid: "videosDuration", name: "مدة الفيديوهات", sortable: false, },
            { uid: "createdAt", name: "أنشئت في", sortable: true },
            { uid: "name", name: "منشئ الكورس", sortable: false },
            { uid: "lecturesCount", name: "عدد المحاضرات", sortable: false },
            { uid: "actions", name: "الافعال", sortable: false },
        ]
        : can("read:users")
            ? [
                { uid: "id", name: "#", sortable: true },
                { uid: "courseName", name: "اسم الكورس", sortable: false },
                { uid: "grade", name: "الصف", sortable: true },
                { uid: "visibility", name: "الحاله", sortable: true },
                { uid: "typeOfCourse", name: "نوع الكورس", sortable: false },
                {
                    uid: "videosDuration",
                    name: "مدة الفيديوهات",
                    sortable: false,
                },
                { uid: "createdAt", name: "أنشئت في", sortable: true },
                { uid: "name", name: "منشئ الكورس", sortable: false },
                { uid: "actions", name: "الافعال", sortable: false },
            ]
            : can("read:course:lectures")
                ? [
                    { uid: "id", name: "#", sortable: true },
                    { uid: "courseName", name: "اسم الوحدة", sortable: false },
                    { uid: "grade", name: "الصف", sortable: true },
                    { uid: "visibility", name: "الحاله", sortable: true },
                    { uid: "typeOfCourse", name: "نوع الكورس", sortable: false },
                    {
                        uid: "videosDuration",
                        name: "مدة الفيديوهات",
                        sortable: false,
                    },
                    { uid: "createdAt", name: "أنشئت في", sortable: true },
                    { uid: "lecturesCount", name: "عدد المحاضرات", sortable: false },
                    { uid: "actions", name: "الافعال", sortable: false },
                ]
                : [
                    { uid: "id", name: "#", sortable: true },
                    { uid: "courseName", name: "اسم الوحدة", sortable: false },
                    { uid: "grade", name: "الصف", sortable: true },
                    { uid: "visibility", name: "الحاله", sortable: true },
                    {
                        uid: "videosDuration",
                        name: "مدة الفيديوهات",
                        sortable: false,
                    },
                    { uid: "createdAt", name: "أنشئت في", sortable: true },
                    { uid: "actions", name: "الافعال", sortable: false },
                ];

    //defaultHeaders
    const INITIAL_VISIBLE_COLUMNS = [
        "id",
        "courseName",
        "grade",
        "visibility",
        "typeOfCourse",
        "name",
        "actions",
    ];

    //Actions
    const actions = useMemo(
        () => [
            {
                id: "rowDetails",
                name: "رؤية",
                icon: <FaEye className="text-lg" />,
                content: (row) => <CourseBox course={row} />,
                permissions: ["read:courses"],
            },
            {
                id: "edit",
                name: "تعديل",
                icon: <MdEdit className="text-lg text-blue-color-primary" />,
                content: (row) => <UpdateCourse oldData={row} />,
                permissions: ["update:courses"],
            },
            {
                id: "delete",
                name: "حذف",
                icon: <MdDelete className="text-lg text-red-color-primary" />,
                permissions: ["delete:courses"],
            },
        ],
        []
    );

    //addRow
    const addRow = {
        row: <CreateForm />,
        permission: can("create:courses"),
    };

    //formatting Data
    const reformattedData = courses?.data?.map((item) => {
        const { id, attributes, relationships, videosDuration, ...rest } = item;

        const courseData = {
            id,
            ...attributes,
            typeOfCourse: attributes?.type,
            courseName: attributes?.name,
            name: relationships?.author?.attributes.name,
            ...relationships?.author?.attributes,
            lecturesCount: relationships?.lecturesCount,
            videosDuration: secondsToTime(videosDuration),
        };

        return {
            ...courseData,
            ...rest,
        };
    });
    const renderCell = useCallback(
        (row, columnKey) => {
            const cellValue = row?.[columnKey];
            switch (columnKey) {
                case "name":
                    return (
                        <User
                            avatarProps={{
                                radius: "full",
                                size: "sm",
                                src: `${STORAGE_LINK}/images/users/${row.image}`,
                            }}
                            classNames={{
                                description: "text-default-500",
                            }}
                            description={row.email}
                            name={cellValue}
                        >
                            {row?.email}
                        </User>
                    );

                case "visibility":
                    return (
                        <Chip
                            className="gap-1 capitalize border-none text-default-600"
                            color={row.visibility ? "success" : "warning"}
                            size="sm"
                            variant="dot"
                        >
                            {cellValue == 1 ? "مفعل" : "غير مفعل"}
                        </Chip>
                    );
                case "courseName":
                    return (
                        <p className="gap-1 capitalize border-none text-default-600 min-w-[200px]">
                            {cellValue}
                        </p>
                    );
                case "grade": {
                    const [key, value] = Object.entries(schoolLevelIds).find(
                        ([, value]) => value === row.school_level
                    ) || [];
                    return key ? `${translatedSchoolGrades[cellValue]} ${translatedSchoolLevels[value]}` : null;
                }
                case "typeOfCourse":
                    return <p>{cellValue == 1 ? "اونلاين" : "سنتر"}</p>;
                case "createdAt":
                    return (
                        <span className="flex flex-col gap-2">
                            <span>
                                {formatDateTime(cellValue).formattedTime}
                            </span>
                            <span>
                                {formatDateTime(cellValue).formattedDate}
                            </span>
                        </span>
                    );
                case "updatedAt":
                    return (
                        <span className="flex flex-col gap-2">
                            <span>
                                {formatDateTime(cellValue).formattedTime}
                            </span>
                            <span>
                                {formatDateTime(cellValue).formattedDate}
                            </span>
                        </span>
                    );
                case "actions":
                    return (
                        <div className="relative flex items-center justify-start gap-2">
                            <Modal>
                                {actions?.map(
                                    (action) =>
                                        canAll(action.permissions) && (
                                            <React.Fragment key={action.id}>
                                                <Modal.Open opens={action.id}>
                                                    <button>
                                                        {action.icon}
                                                    </button>
                                                </Modal.Open>
                                                <Modal.Window name={action.id}>
                                                    {action.id ===
                                                        "rowDetails" ? (
                                                        action.content(row)
                                                    ) : action.id === "edit" ? (
                                                        action.content(row)
                                                    ) : action.id ===
                                                        "delete" ? (
                                                        <ConfirmDelete
                                                            resourceName={
                                                                row?.courseName
                                                            }
                                                            disabled={
                                                                isDeleting
                                                            }
                                                            onConfirm={() =>
                                                                deleteCourse(
                                                                    row?.id
                                                                )
                                                            }
                                                        />
                                                    ) : null}
                                                </Modal.Window>
                                            </React.Fragment>
                                        )
                                )}
                            </Modal>
                        </div>
                    );
                default:
                    return cellValue;
            }
        },
        [actions, isDeleting, deleteCourse, canAll]
    );

    const [visibleColumns, setVisibleColumns] = useState(
        new Set(INITIAL_VISIBLE_COLUMNS)
    );

    const totalPages = Math.ceil(
        courses?.meta?.total / courses?.meta?.per_page
    );

    return (
        <Table
            isloading={isPending}
            rows={reformattedData}
            headers={headers}
            visibleColumns={visibleColumns}
            renderCell={renderCell}
            columnsToCopy={["name", "courseName", "grade"]}
            topContent={
                <HeaderContent
                    filterOptions={filterOptions}
                    filterButtons={filterButtons}
                    visibleColumns={visibleColumns}
                    setVisibleColumns={setVisibleColumns}
                    headers={headers}
                    rowsNumber={perPage}
                    setRowsNumber={setPerPage}
                    setSearchValue={setSearchValue}
                    placeholder="يمكنك البحث بأسم الفصل الدراسي او الوصف او ID"
                    addRow={addRow}
                    totalRows={courses?.meta?.total}
                    modalWidth="w-full md:w-[90%] lg:w-1/2"
                />
            }
            bottomContent={<FooterContent totalPages={totalPages} />}
        />
    );
}
export default CoursesTable;
